import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { IoClose } from 'react-icons/io5';
import { ProductsApiService } from '../../../../service/website/productsApiService';
import { CartApiService } from '../../../service/cartApiService';
import { useDispatch } from 'react-redux';
import { fetchCartItems } from '../../../../redux/slices/cartSlice';
import { appendSizeToImageUrl } from '../../../../utils/imageUtils';
import { ToastTypes, useMessage } from '../../../../base/context/MessageProvider';
import { SellingModes } from '../../../../helper/constants';

const ChangeVariantModal = ({
  isOpen,
  onClose,
  product,
  currentVariants,
  cartItemId,
  pricingMode,
}) => {
  const [loading, setLoading] = useState(false);
  const [allVariations, setAllVariations] = useState([]);
  const [quantities, setQuantities] = useState({}); // { skuId: number }
  const [selectedColor, setSelectedColor] = useState('');
  const [saving, setSaving] = useState(false);
  const hasInitializedRef = useRef(false);
  const dispatch = useDispatch();
  const message = useMessage();

  const isWholesale = pricingMode === SellingModes.Wholesale;

  /* ------------------------------------------------------------------ */
  /*  Build attribute groups from raw variations                         */
  /* ------------------------------------------------------------------ */
  const { colorGroup, sizeGroup, variantsByCombo } = useMemo(() => {
    if (!allVariations || allVariations.length === 0)
      return { colorGroup: null, sizeGroup: null, variantsByCombo: {} };

    const attrMap = new Map(); // attributeId -> { id, name, nameTrans, isColor, values: Map }

    allVariations.forEach((v) => {
      (v.skuAttributes || []).forEach((attr) => {
        const id = attr.attributeId;
        const valKey = attr.valueTrans || attr.value;

        if (!attrMap.has(id)) {
          attrMap.set(id, {
            id,
            name: attr.attributeName,
            nameTrans: attr.attributeNameTrans || attr.attributeName,
            isColor: !!attr.isColor,
            values: new Map(),
          });
        }
        const grp = attrMap.get(id);
        if (!grp.values.has(valKey)) {
          grp.values.set(valKey, {
            value: attr.value,
            valueTrans: valKey,
            imageUrl: attr.skuImageUrl || '',
            variants: [],
          });
        }
        grp.values.get(valKey).variants.push(v);
      });
    });

    const groups = Array.from(attrMap.values());
    // first group that is "color" (has images), rest is "size"
    const cg = groups.find((g) => g.isColor) || (groups.length > 1 ? groups[0] : null);
    const sg = groups.find((g) => g !== cg) || (groups.length === 1 ? groups[0] : null);

    // build lookup: "colorValue|sizeValue" -> variant
    const combo = {};
    allVariations.forEach((v) => {
      const colorAttr = cg
        ? (v.skuAttributes || []).find((a) => String(a.attributeId) === String(cg.id))
        : null;
      const sizeAttr = sg
        ? (v.skuAttributes || []).find((a) => String(a.attributeId) === String(sg.id))
        : null;
      const key = `${colorAttr ? colorAttr.valueTrans || colorAttr.value : '_'}|${sizeAttr ? sizeAttr.valueTrans || sizeAttr.value : '_'}`;
      combo[key] = v;
    });

    return { colorGroup: cg, sizeGroup: sg, variantsByCombo: combo };
  }, [allVariations]);

  /* ------------------------------------------------------------------ */
  /*  Init quantities & color from current cart variants — ONCE only     */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    // Only initialize when modal opens, never re-run while it's open
    if (!isOpen) {
      // Reset the flag when modal closes so next open re-initializes
      hasInitializedRef.current = false;
      return;
    }
    if (hasInitializedRef.current) return;

    const initQty = {};
    let initColor = '';

    (currentVariants || []).forEach((v) => {
      if (v.skuId && v.quantity > 0) initQty[v.skuId] = v.quantity;
      if (!initColor && v.skuAttributes && colorGroup) {
        const ca = v.skuAttributes.find((a) => String(a.attributeId) === String(colorGroup.id));
        if (ca) initColor = ca.valueTrans || ca.value;
      }
    });

    setQuantities(initQty);
    if (initColor) setSelectedColor(initColor);
    hasInitializedRef.current = true;
  }, [isOpen, currentVariants, colorGroup]);

  // Auto-select first color once variations load
  useEffect(() => {
    if (colorGroup && !selectedColor) {
      const firstColor = Array.from(colorGroup.values.keys())[0];
      if (firstColor) setSelectedColor(firstColor);
    }
  }, [colorGroup, selectedColor]);

  /* ------------------------------------------------------------------ */
  /*  Fetch variations                                                   */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (isOpen && product) fetchVariations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, product]);

  const fetchVariations = async () => {
    try {
      setLoading(true);
      const offerId = product?.offerId || product?.offer_id || product?.sku;
      if (!offerId) { message.showToast('Product ID not found', ToastTypes.ERROR); return; }
      const res = await ProductsApiService.getProductByOfferId(offerId);
      if (res?.status?.code === 200 && res?.data) {
        setAllVariations(res.data.variations || []);
      } else {
        message.showToast('Failed to load variations', ToastTypes.ERROR);
      }
    } catch (err) {
      console.error('Error fetching variations:', err);
      message.showToast('Error loading variations', ToastTypes.ERROR);
    } finally {
      setLoading(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /*  Helpers: get variant for current color + size, qty accessors       */
  /* ------------------------------------------------------------------ */
  const getVariant = useCallback(
    (sizeKey) => {
      return variantsByCombo[`${selectedColor || '_'}|${sizeKey || '_'}`] || null;
    },
    [selectedColor, variantsByCombo]
  );

  const getQty = (skuId) => quantities[skuId] || 0;

  const setQty = (skuId, val) => {
    setQuantities((prev) => {
      const next = { ...prev };
      if (val <= 0) {
        delete next[skuId];
      } else {
        next[skuId] = val;
      }
      return next;
    });
  };

  /* ------------------------------------------------------------------ */
  /*  Save to cart — REPLACE all variants for this product               */
  /* ------------------------------------------------------------------ */
  const handleSave = async () => {
    // Build the full list: variants with qty > 0 keep their qty
    const variantsWithQty = [];
    const variantsToRemove = [];

    // First, collect all variants user wants to keep (qty > 0)
    allVariations.forEach((v) => {
      const qty = quantities[v.skuId] || 0;
      if (qty > 0) {
        variantsWithQty.push({ skuId: v.skuId, quantity: qty });
      }
    });

    if (variantsWithQty.length === 0) {
      message.showToast('Please select at least one variant', ToastTypes.WARNING);
      return;
    }

    // Find variants that WERE in the cart but are now set to 0
    (currentVariants || []).forEach((cv) => {
      const newQty = quantities[cv.skuId] || 0;
      if (newQty === 0 && cv.quantity > 0) {
        variantsToRemove.push(cv);
      }
    });

    setSaving(true);
    try {
      // Step 1: Remove variants that went to 0
      for (const rv of variantsToRemove) {
        await CartApiService.RemoveFromCart(
          JSON.stringify({
            productId: product._id,
            skuId: rv.skuId,
            pricingMode,
          })
        );
      }

      // Step 2: Update cart with the remaining variants (qty > 0)
      const payload = {
        offerId: product.offerId || product.offer_id || product.sku,
        variants: variantsWithQty,
        pricingMode,
        quantityFromCart: true,
      };
      await CartApiService.AddUpdateCart(JSON.stringify(payload));

      await dispatch(fetchCartItems());
      message.showToast('Variants updated successfully', ToastTypes.SUCCESS);
      onClose();
    } catch (err) {
      console.error('Error updating variants:', err);
      message.showToast('Failed to update variants', ToastTypes.ERROR);
    } finally {
      setSaving(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /*  Computed totals                                                     */
  /* ------------------------------------------------------------------ */
  const totalUnits = Object.values(quantities).reduce((s, q) => s + q, 0);
  const selectedCount = Object.values(quantities).filter((q) => q > 0).length;

  /* ------------------------------------------------------------------ */
  /*  Render nothing when closed                                         */
  /* ------------------------------------------------------------------ */
  if (!isOpen) return null;

  /* ------------------------------------------------------------------ */
  /*  Size rows to display (filtered by selected color)                  */
  /* ------------------------------------------------------------------ */
  const sizeRows = [];
  if (sizeGroup) {
    Array.from(sizeGroup.values.entries()).forEach(([sizeKey]) => {
      const variant = getVariant(sizeKey);
      if (variant) sizeRows.push({ sizeKey, variant });
    });
  } else if (!colorGroup && allVariations.length > 0) {
    // single-attribute product (only color / only size)
    const onlyGroup = colorGroup || sizeGroup;
    if (!onlyGroup) {
      // no attributes at all — just list all variations
      allVariations.forEach((v) => {
        sizeRows.push({ sizeKey: v.skuId, variant: v });
      });
    }
  }

  // If there's only one group (no color separate from size) treat every variation as a row
  const isSingleGroup = !colorGroup && sizeGroup;
  const effectiveRows = isSingleGroup
    ? Array.from(sizeGroup.values.entries()).map(([sizeKey, data]) => ({
      sizeKey,
      variant: data.variants[0],
    }))
    : sizeRows;

  return (
    <div className="cvm-overlay" onClick={onClose}>
      <div className="cvm-modal" onClick={(e) => e.stopPropagation()}>
        {/* -------- Header -------- */}
        <div className="cvm-header">
          <h2 className="cvm-header-title">Change Variant</h2>
          <button className="cvm-close-btn" onClick={onClose} disabled={saving}>
            <IoClose size={20} />
          </button>
        </div>

        {/* -------- Product strip -------- */}
        <div className="cvm-product-strip">
          <img
            src={appendSizeToImageUrl(product?.imgUrl?.[0], '80x80')}
            alt=""
            referrerPolicy="no-referrer"
            className="cvm-product-img"
          />
          <div className="cvm-product-info">
            <p className="cvm-product-title">{product?.title}</p>
            <p className="cvm-product-meta">
              {selectedCount} variant(s) &middot; {totalUnits} units
            </p>
          </div>
        </div>

        {/* -------- Body -------- */}
        <div className="cvm-body">
          {loading ? (
            <div className="cvm-loading">
              <div className="cvm-spinner" />
              <span>Loading variations&hellip;</span>
            </div>
          ) : (
            <>
              {/* ---------- Color Section ---------- */}
              {colorGroup && (
                <div className="cvm-section">
                  <h3 className="cvm-section-title">{colorGroup.nameTrans}</h3>
                  <div className="cvm-color-list">
                    {Array.from(colorGroup.values.entries()).map(([valKey, valData]) => {
                      const active = selectedColor === valKey;
                      return (
                        <div
                          key={valKey}
                          className={`cvm-color-card ${active ? 'cvm-color-card--active' : ''}`}
                          onClick={() => setSelectedColor(valKey)}
                        >
                          {valData.imageUrl && (
                            <img
                              src={appendSizeToImageUrl(valData.imageUrl, '60x60')}
                              alt=""
                              referrerPolicy="no-referrer"
                              className="cvm-color-img"
                            />
                          )}
                          <span className="cvm-color-label">{valData.valueTrans}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ---------- Size / Variant rows ---------- */}
              {effectiveRows.length > 0 && (
                <div className="cvm-section">
                  <h3 className="cvm-section-title">
                    {sizeGroup ? sizeGroup.nameTrans : 'Variants'}
                  </h3>
                  <div className="cvm-size-list">
                    {effectiveRows.map(({ sizeKey, variant }) => {
                      const skuId = variant.skuId;
                      const qty = getQty(skuId);
                      const stock = variant.amountOnSale || 0;
                      const price = Number(
                        isWholesale
                          ? variant?.calculatedFBP || variant.wholesaleFinalPrice || variant.finalPrice || variant.price || 0
                          : variant.finalPrice || variant.price || 0
                      );

                      return (
                        <div
                          key={skuId}
                          className={`cvm-size-row ${qty > 0 ? 'cvm-size-row--selected' : ''}`}
                        >
                          {/* Left info */}
                          <div className="cvm-size-info">
                            <span className="cvm-size-name">{sizeKey}</span>
                            <span className="cvm-size-specs">
                              Wt : {(variant.weight ?? 0).toFixed(3)}kg &nbsp;|&nbsp;
                              L : {(variant.length ?? 0).toFixed(2)}cm &nbsp;|&nbsp;
                              W : {(variant.width ?? 0).toFixed(2)}cm &nbsp;|&nbsp;
                              H : {(variant.height ?? 0).toFixed(2)}cm
                            </span>
                          </div>

                          {/* Price */}
                          <div className="cvm-size-price">
                            <span className="cvm-price-value">
                              ₹{price.toFixed(2)} /
                            </span>
                            <span className="cvm-price-stock">
                              {stock} units in stock
                            </span>
                          </div>

                          {/* Qty controls */}
                          <div className="cvm-qty-wrap">
                            <button
                              type="button"
                              className="cvm-qty-btn"
                              disabled={qty <= 0}
                              onClick={() => setQty(skuId, qty - 1)}
                            >
                              −
                            </button>
                            <input
                              type="text"
                              inputMode="numeric"
                              className="cvm-qty-input"
                              value={qty}
                              onChange={(e) => {
                                const v = parseInt(e.target.value, 10);
                                if (isNaN(v)) { setQty(skuId, 0); return; }
                                if (v > stock) {
                                  message.showToast(`Max stock is ${stock}`, ToastTypes.WARNING);
                                  setQty(skuId, stock);
                                  return;
                                }
                                setQty(skuId, v);
                              }}
                            />
                            <button
                              type="button"
                              className="cvm-qty-btn"
                              disabled={qty >= stock}
                              onClick={() => {
                                if (qty >= stock) {
                                  message.showToast('Max stock reached', ToastTypes.WARNING);
                                  return;
                                }
                                setQty(skuId, qty + 1);
                              }}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {effectiveRows.length === 0 && !loading && (
                <p className="cvm-empty">No variants found for this selection.</p>
              )}
            </>
          )}
        </div>

        {/* -------- Footer -------- */}
        <div className="cvm-footer">
          <span className="cvm-footer-total">
            Total: <strong>{totalUnits}</strong> units
          </span>
          <div className="cvm-footer-actions">
            <button className="cvm-btn cvm-btn--outline" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button
              className="cvm-btn cvm-btn--primary"
              onClick={handleSave}
              disabled={saving || selectedCount === 0}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangeVariantModal;
